import React, { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useModal } from '../../contexts/ModalContext';
import { PleriModal } from '../common/PleriModal';
import { useDeleteConversation } from '../../api/conversations';
import { Button } from '@plerionhq/design-system';

import { useTranslation } from 'react-i18next';

export const DeleteConversationModal: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const navigate = useNavigate();
  const { deleteModalOpen, conversationToDelete, closeDeleteModal } =
    useModal();
  const { mutate: deleteConversation, isPending: isDeleting } =
    useDeleteConversation();
  const [isDeletingState, setIsDeletingState] = useState(false);

  const confirmDeleteConversation = () => {
    if (!conversationToDelete || isDeletingState) {
      return;
    }

    setIsDeletingState(true);

    // Call the delete mutation
    deleteConversation(conversationToDelete, {
      onSuccess: () => {
        setIsDeletingState(false);
        closeDeleteModal();
        // Navigate to a new conversation after successful deletion
        navigate({
          to: '/c/$conversationId',
          params: { conversationId: 'new' },
        });
      },
      onError: () => {
        setIsDeletingState(false);
        closeDeleteModal();
      },
    });
  };

  const isLoading = isDeleting || isDeletingState;

  return (
    <PleriModal
      isOpen={deleteModalOpen}
      title={t('deleteChat')}
      onClose={closeDeleteModal}
      size="sm"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('areYouSureYouWantToDeleteThisChat')}
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            variant="secondary"
            label={t('common:cancel')}
            data-testid="cancel-delete-conversation"
            onClick={closeDeleteModal}
            isDisabled={isLoading}
            size="small"
          />
          <Button
            label={isLoading ? t('deleting') : t('common:delete')}
            data-testid="confirm-delete-conversation"
            onClick={confirmDeleteConversation}
            isDisabled={isLoading}
            variant="primary"
            size="small"
          />
        </div>
      </div>
    </PleriModal>
  );
};
